/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import React, {
  useState,
  useRef,
  useEffect,
  memo,
  useMemo,
  useCallback,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';

// Icons
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import FastForwardIcon from '@material-ui/icons/FastForward';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import ReplayIcon from '@material-ui/icons/Replay';

// Leaflet
import L from 'leaflet';
import * as ReactLeaflet from 'react-leaflet';
import Control from 'react-leaflet-control';
import * as MarkerIcons from 'components/Leaflet/MarkerIcons';

// React Inline SVG
import SVG from 'react-inlinesvg';

// Clsx
import clsx from 'clsx';

// Use Ref State
import useRefState from 'helpers/useRefState';

// Lodash
import { debounce } from 'lodash';

// Binary Search Bounds
import bounds from 'binary-search-bounds';

// Helpers
import * as LeafletHelpers from 'helpers/leaflet';
import { getJsonData } from 'helpers/getJsonData';
import { getTripId } from 'helpers/trips';

// Constants
import MAP_CONSTANTS from 'constants/map';
import { CONST_LINES_OBJECT } from 'constants/lines';
import IMAGES from 'constants/images';

// Actions
import { getLineRequest } from 'store/ducks/lines/actions';

// Types
import { ApplicationState } from 'store';
import { TripInterface } from 'store/ducks/trips/types';
import { ChipsInterface } from 'store/ducks/stops/types';

// Hooks
import useMount from 'hooks/useMount';

// Components
import TripInfoDrawer from '../InfoDrawer/Trip';
import StopInfoDrawer from '../InfoDrawer/Stop';

// Styles
import useStyles from './mapTab.styles';

const INIT_VALUE_STOP_DRAWER = { id: '', open: false };
const INIT_VALUE_TRIP_DRAWER = { data: null as any, open: false };

interface ICurrentTrip {
  tripId: string;
  lineSectionId: string;
  coordinates: L.LatLngExpression;
}

interface MapTabProps {
  isPause?: boolean;
  lineFilterRemoved?: boolean;
  setIsPause: React.Dispatch<React.SetStateAction<boolean>>;
  filterList: ChipsInterface[] | [];
  selectedLines: any[];
  children?: React.ReactNode | React.ReactElement;
  currentTrips: ICurrentTrip[];
  setCurrentTrips: (param: ICurrentTrip[]) => void;
  setTripMapInstance?: (
    value: React.SetStateAction<ReactLeaflet.Map<
      ReactLeaflet.MapProps,
      L.Map
    > | null>,
  ) => void;
}

type Props = MapTabProps;

const MapTab: React.FC<Props> = ({
  isPause,
  lineFilterRemoved,
  setIsPause,
  filterList,
  selectedLines,
  children,
  currentTrips,
  setCurrentTrips,
  setTripMapInstance,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tripId = getTripId();

  const mount = useMount();

  const playerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [mapInstance, setMapInstance] = useState<ReactLeaflet.Map | null>(null);
  const [fetch, setFetch] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [autoZoom, setAutoZoom] = useState(true);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [tripInfoDrawer, setTripInfoDrawer] = useState(INIT_VALUE_TRIP_DRAWER);
  const [stopInfoDrawer, setStopInfoDrawer] = useState(INIT_VALUE_STOP_DRAWER);
  const [tripData, setTripData] = useState<TripInterface | null>(null);
  const [selectedTrips, setSelectedTrips] = useState<TripInterface[]>([]);

  const [showLines, setShowLines] = useState(true);
  const [showStops, setShowStops] = useState(true);
  const [showCars, setShowCars] = useState(true);

  const [
    currentTimeIndex,
    currentTimeIndexRef,
    setCurrentTimeIndex,
  ] = useRefState<number>(-1);

  const initialTripList = useSelector(
    (state: ApplicationState) => state.trips.tripList,
  );

  const initialTripData = useSelector(
    (state: ApplicationState) => state.trips.tripData,
  );

  const lineData = useSelector(
    (state: ApplicationState) => state.lines.lineData,
  );

  const setControlMapStyle = (control: any) => {
    control.style = 'margin: 0; width: 100%;';
    control.parentNode.style = 'width: 100%;';
  };

  useEffect(() => {
    setTripData(initialTripData);
    return () => {
      setTripData(null);
      setSelectedTrips([]);
    };
  }, [initialTripData]);

  useEffect(() => {
    if (tripData?.id) {
      setSelectedTrips([tripData]);
    } else {
      setSelectedTrips(initialTripList);
    }
  }, [tripData, initialTripList]);

  useEffect(() => {
    if (mapInstance && mapInstance?.leafletElement && !tripId) {
      const lfMap = mapInstance?.leafletElement;

      if (filterList && filterList?.length > 0 && selectedTrips.length > 0) {
        const section = selectedTrips.find(
          async ({ lineSectionId, LineSection }) =>
            selectedLines
              ?.flatMap(({ Sections }: any) =>
                Sections?.map(({ LineSection }: any) => LineSection.id),
              )
              ?.find((id) => id === lineSectionId && LineSection?.route),
        ) as any;

        (async () => {
          try {
            if (
              section.LineSection.route &&
              selectedLines &&
              selectedLines?.length > 0
            ) {
              if (fetch) {
                const geojson = await getJsonData(section.LineSection.route);
                if (geojson) {
                  setFetch(false);
                  await LeafletHelpers.loadGeoJsonLine({
                    mapRef: lfMap,
                    geojson,
                    selectedLines,
                    toggleInfoDrawerPoint,
                  });
                }
              }
            }

            if (selectedLines?.length === 0) {
              LeafletHelpers.clearMap(lfMap as L.Map);
            }
          } catch (error) {
            // Nothing
          }
        })();
      }

      if (lineFilterRemoved) {
        LeafletHelpers.clearMap(lfMap as L.Map);
      }
    } else if (mapInstance && mapInstance?.leafletElement && tripId) {
      const lfMap = mapInstance?.leafletElement;

      (async () => {
        try {
          if (selectedTrips?.[0]?.LineSection?.route) {
            const geojson = await getJsonData(
              String(selectedTrips[0].LineSection.route),
            );
            if (geojson) {
              await LeafletHelpers.loadGeoJsonLine({
                mapRef: lfMap,
                geojson,
                selectedLines: [lineData],
                toggleInfoDrawerPoint,
              });
            }
            if (!lineData?.id) {
              dispatch(
                getLineRequest({
                  body: {
                    id: selectedTrips[0].LineSection.Line.id,
                    _includes: 'linestop',
                  },
                }),
              );
            }
          }
        } catch (error) {
          // Nothing
        }
      })();
    }
  }, [
    tripId,
    filterList,
    lineFilterRemoved,
    mapInstance,
    selectedLines,
    lineData,
    selectedTrips,
    fetch,
    dispatch,
  ]);

  /** Get the last sample(index) that has timestamp less than or equal to time */
  const getLastSampleOfTime = (tripSamples: any[], time: number) => {
    // uses binary search
    const index = bounds.le(
      tripSamples,
      { timestamp: time } as any,
      (a, b) => a.timestamp - b.timestamp,
    );
    return index;
  };

  /** Get current line sections without repeating */
  const getCurrentLineSections = (
    allTrips: TripInterface[],
    currTrips: ICurrentTrip[],
  ) => {
    const sections = new Map(
      allTrips
        .filter(({ lineSectionId }) =>
          currTrips.some((t) => t.lineSectionId === lineSectionId),
        )
        .map(({ LineSection, LineRoute }) => [
          LineSection?.id,
          { LineSection, Route: LineRoute?.route },
        ]),
    );
    return Array.from(sections).map(([id, lineSection]) => lineSection);
  };

  /** Get current stops without repeating */
  const getCurrentStops = (
    allTrips: TripInterface[],
    currTrips: ICurrentTrip[],
  ) => {
    const stops = new Map(
      allTrips
        .filter(({ lineSectionId }) =>
          currTrips.some((t) => t.lineSectionId === lineSectionId),
        )
        .flatMap(({ LineRoute }) => {
          if (LineRoute?.stops) {
            return LineRoute!.stops?.map(({ properties, ...rest }) => [
              properties.id,
              { properties, ...rest },
            ]);
          }
          return [];
        }),
    );
    return Array.from(stops).map(([id, stop]) => stop);
  };

  const timeIsInRange = useMemo(
    () => (timeIndex: number) =>
      timeIndex >= 0 && timeIndex <= timestamps.length - 1,
    [timestamps],
  );

  const updateTimestamps = useCallback(() => {
    const updatedTimes = Array.from(
      new Set(
        selectedTrips.flatMap(({ TripRoute }) =>
          (TripRoute || []).map(({ timestamp }) => timestamp),
        ),
      ),
    ).sort();
    setCurrentTimeIndex(0);
    setTimestamps(updatedTimes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList, selectedTrips, setCurrentTimeIndex]);

  const debouncedUpdateTrips = useMemo(
    () =>
      debounce((timeIndex: number) => {
        const time = timestamps[timeIndex];
        const updatedTrips = [...currentTrips];

        selectedTrips.forEach(({ id, lineSectionId, TripRoute }) => {
          if (!TripRoute) return;
          const route = TripRoute;
          const firstTime = route[0]?.timestamp;
          const lastTime = route[route.length - 1]?.timestamp;
          const index = currentTrips.findIndex(({ tripId }) => tripId === id);
          const isRendered = index >= 0;
          const hasTimestamp = time >= firstTime && time <= lastTime;
          // update trip
          if (isRendered && hasTimestamp) {
            const i = getLastSampleOfTime(route, time);
            const { lat, lng } = route[i];
            updatedTrips[index] = {
              ...updatedTrips[index],
              coordinates: { lat, lng },
            };
          }
          // add trip
          if (!isRendered && hasTimestamp) {
            const i = getLastSampleOfTime(route, time);
            const { lat, lng } = route[i];
            updatedTrips.push({
              tripId: id,
              lineSectionId: lineSectionId as string,
              coordinates: { lat, lng },
            });
          }
          // remove trip
          if (isRendered && !hasTimestamp) {
            updatedTrips.splice(index, 1);
          }
        });

        if (setCurrentTrips && updatedTrips?.length > 0) {
          setCurrentTrips(updatedTrips);
        }
      }, 500),
    [timestamps, currentTrips, selectedTrips, setCurrentTrips],
  );

  const handleUpdateTrips = useCallback(
    (timeIndex: number) => {
      if (timeIsInRange(timeIndex)) {
        setCurrentTimeIndex(timeIndex);
        debouncedUpdateTrips(timeIndex);
      }
    },
    [debouncedUpdateTrips, setCurrentTimeIndex, timeIsInRange],
  );

  const handleClickPlay = useCallback(
    (value: boolean) => {
      if (value) {
        setIsPause(false);
        playerTimerRef.current = setInterval(() => {
          const time = currentTimeIndexRef.current + 1;
          if (timeIsInRange(time)) handleUpdateTrips(time);
          else if (playerTimerRef.current) {
            setIsPlaying(false);
            clearInterval(playerTimerRef.current);
          }
        }, 1000);
      } else if (playerTimerRef.current) {
        clearInterval(playerTimerRef.current);
      }
      setIsPlaying(value);
    },
    [currentTimeIndexRef, handleUpdateTrips, setIsPause, timeIsInRange],
  );

  const toggleStopInfoDrawer = (open: boolean, stopId: string) => (
    event: any,
  ) => {
    if (open) setSelectedStopId(stopId);
    else setSelectedStopId(null);

    if (stopInfoDrawer.open) {
      setStopInfoDrawer({ id: '', open: false });
      return;
    }

    if (!stopId) return;

    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setStopInfoDrawer({ id: open ? stopId : '', open });
  };

  const toggleTripInfoDrawer = (open: boolean, trip: any) => (event: any) => {
    if (open && trip?.id) setSelectedTripId(trip.id);
    else setSelectedTripId(null);

    if (tripInfoDrawer.open) {
      setTripInfoDrawer({ data: null, open: false });
      return;
    }

    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setTripInfoDrawer({ data: trip, open });
  };

  const toggleInfoDrawerPoint = (open: boolean, id: any, stopData: any) => {
    setStopInfoDrawer({ id: stopData?.id || '', open });
  };

  const handleChangeZoomMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoZoom(event.target.checked);
    },
    [],
  );

  useEffect(() => {
    if (isPause) {
      handleClickPlay(!isPlaying);
    }
  }, [handleClickPlay, isPause, isPlaying]);

  useEffect(() => {
    if (setCurrentTrips) {
      setCurrentTrips([]);
    }
    updateTimestamps();
  }, [selectedTrips, setCurrentTrips, updateTimestamps]);

  useEffect(() => {
    if (currentTrips?.length === 0) {
      handleClickPlay(false);
      setCurrentTimeIndex(0);
    }
  }, [currentTrips, handleClickPlay, handleUpdateTrips, setCurrentTimeIndex]);

  const centerMap = useCallback(() => {
    if (mapInstance) {
      setTimeout(() => {
        if (mapInstance?.leafletElement) {
          LeafletHelpers.centerMap(mapInstance?.leafletElement);
        }
      }, 800);
    }
  }, [mapInstance]);

  useEffect(() => {
    if (autoZoom) {
      centerMap();
    }
    // eslint-disable-next-line
  }, [autoZoom, currentTrips]);

  const handleToggleLines = () => {
    setShowLines((show) => !show);
    if (mapInstance) {
      LeafletHelpers.toggleLinesFromMap();
    }
  };

  const handleToggleStops = () => {
    setShowStops((show) => !show);
    if (mapInstance) {
      LeafletHelpers.toggleMarkersFromMap();
    }
  };

  const handleToggleCars = () => {
    setShowCars((show) => !show);
  };

  if (!mount) return null;

  return (
    <>
      <StopInfoDrawer
        id={stopInfoDrawer.id}
        open={stopInfoDrawer.open}
        toggleDrawer={toggleStopInfoDrawer}
      />
      <TripInfoDrawer
        open={tripInfoDrawer.open}
        tripData={tripInfoDrawer.data}
        toggleDrawer={toggleTripInfoDrawer}
      />
      <Box className={classes.mapBox}>
        <ReactLeaflet.Map
          zoom={5}
          ref={(ref) => {
            setMapInstance(ref);
            if (setTripMapInstance) setTripMapInstance(ref);
          }}
          fullscreenControl
          className={classes.leafletMap}
          center={MAP_CONSTANTS.DEFAULT_CENTER as any}>
          <ReactLeaflet.TileLayer
            url={MAP_CONSTANTS.CONTRIBUTION_ICON}
            attribution={MAP_CONSTANTS.CONTRIBUTION_LINK}
          />
          {currentTrips &&
            getCurrentLineSections(selectedTrips, currentTrips).map(
              ({ LineSection, Route }) => {
                const color = LineSection?.Line.color;
                const way =
                  LineSection?.way === CONST_LINES_OBJECT.WAYS.GO.KEY
                    ? 'IDA'
                    : 'VOLTA';
                const lineName = `${LineSection?.Line.name} - ${way}`;
                return (
                  Route && (
                    <ReactLeaflet.Polyline
                      key={LineSection?.id}
                      color={color}
                      positions={Route.coordinates}>
                      <ReactLeaflet.Popup>{lineName}</ReactLeaflet.Popup>
                    </ReactLeaflet.Polyline>
                  )
                );
              },
            )}
          {currentTrips &&
            getCurrentStops(selectedTrips, currentTrips).map(
              ({ properties, coordinates }) => {
                const color =
                  properties.id === selectedStopId ? '#FFD326' : '#555';
                return (
                  <ReactLeaflet.CircleMarker
                    key={properties.id}
                    radius={5}
                    center={coordinates}
                    color={color}
                    fillColor={color}
                    fillOpacity={0.8}
                    onclick={toggleStopInfoDrawer(true, properties.id)}
                    ref={(ref) => ref?.leafletElement.bringToFront()}
                  />
                );
              },
            )}
          {/* {showCars &&
            currentTrips.map(({ tripId, coordinates }) => {
              const tripData = selectedTrips.find((t) => t.id === tripId);

              const icon =
                tripId === selectedTripId
                  ? MarkerIcons.yellowBus
                  : MarkerIcons.blackBus;
              return (
                <ReactLeaflet.Marker
                  key={tripId}
                  icon={icon}
                  position={coordinates}
                  onclick={toggleTripInfoDrawer(true, tripData)}
                />
              );
            })} */}
          <Control position='topright'>
            <Grid container direction='column'>
              <Button
                className={clsx(classes.toggleBtn, {
                  [classes.toggleBtnNoActive]: !showLines,
                })}
                onClick={handleToggleLines}>
                <SVG
                  src={IMAGES.ICONS.LINE}
                  title='linhas'
                  className={classes.icon}
                />
                <Typography className={classes.title}>Linhas</Typography>
              </Button>
              <Button
                className={clsx(classes.toggleBtn, {
                  [classes.toggleBtnNoActive]: !showStops,
                })}
                onClick={handleToggleStops}>
                <SVG
                  src={IMAGES.ICONS.STOP}
                  title='paradas'
                  className={classes.icon}
                />
                <Typography className={classes.title}>Paradas</Typography>
              </Button>
              <Button
                className={clsx(classes.toggleBtn, {
                  [classes.toggleBtnNoActive]: !showCars,
                })}
                onClick={handleToggleCars}>
                <SVG
                  src={IMAGES.ICONS.BUS}
                  title='veiculos'
                  className={classes.icon}
                />
                <Typography className={classes.title}>Veículos</Typography>
              </Button>
            </Grid>
          </Control>
          {showCars &&
            currentTrips?.map(({ tripId, coordinates }) => {
              const tripData = selectedTrips.find((t) => t.id === tripId);

              const icon =
                tripId === selectedTripId
                  ? MarkerIcons.yellowBus
                  : MarkerIcons.blackBus;
              return (
                <ReactLeaflet.Marker
                  key={tripId}
                  icon={icon}
                  position={coordinates}
                  onClick={toggleTripInfoDrawer(true, tripData)}
                />
              );
            })}
          {children && <>{children}</>}
          <Control
            position='bottomleft'
            ref={(ref: any) => {
              if (ref) setControlMapStyle(ref.leafletElement._container);
            }}>
            <Grid className={classes.autoZoom}>
              <Typography>Zoom Automático</Typography>
              <Tooltip
                title={`Auto zoom ${autoZoom ? 'habilitado' : 'desabilitado'}`}>
                <Switch
                  name='auto zoom'
                  color='secondary'
                  checked={autoZoom}
                  onChange={handleChangeZoomMode}
                  inputProps={{ 'aria-label': 'toggle auto zoom' }}
                />
              </Tooltip>
            </Grid>
            <Grid container alignItems='center'>
              <Grid item xs={12}>
                <Box px={2} py={2}>
                  <Slider
                    step={1}
                    min={0}
                    max={timestamps.length > 0 ? timestamps.length - 1 : 0}
                    defaultValue={0}
                    value={currentTimeIndex}
                    className={classes.slider}
                    onChange={(e, v) => handleUpdateTrips(v as number)}
                  />
                </Box>
              </Grid>
            </Grid>
          </Control>
        </ReactLeaflet.Map>
      </Box>
      <Box className={classes.controlBarBox}>
        <Grid container alignItems='center' alignContent='center'>
          <Grid item xs={3}>
            <Grid container alignItems='center'>
              <Grid item>
                <Box component='span' pl={1}>
                  {timeIsInRange(currentTimeIndex) ? (
                    <Typography component='span' style={{ color: '#fff' }}>
                      {new Date(timestamps[currentTimeIndex]).toLocaleString(
                        'pt-BR',
                      )}
                    </Typography>
                  ) : (
                    <></>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs>
            <Grid container justifyContent='center'>
              <Grid item>
                <IconButton
                  size='small'
                  className={classes.controlButton}
                  disabled={currentTimeIndex === 0}
                  onClick={() => handleUpdateTrips(currentTimeIndex - 1)}>
                  <FastRewindIcon fontSize='large' />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  size='small'
                  className={classes.controlButton}
                  onClick={() => handleClickPlay(!isPlaying)}>
                  {isPlaying ? (
                    <PauseCircleFilledIcon fontSize='large' />
                  ) : (
                    <PlayCircleFilledIcon fontSize='large' />
                  )}
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  size='small'
                  className={classes.controlButton}
                  disabled={currentTimeIndex === timestamps.length - 1}
                  onClick={() => handleUpdateTrips(currentTimeIndex + 50)}>
                  <FastForwardIcon fontSize='large' />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  size='small'
                  className={classes.controlButton}
                  onClick={() => handleUpdateTrips(0)}>
                  <ReplayIcon fontSize='large' />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3} />
        </Grid>
      </Box>
    </>
  );
};

export default memo(MapTab);
